import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import type { ClientServicePreferences } from "../helpers/clientServicePreferences";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

export type { ClientServicePreferences };

const api = new APIClient();

const RESOURCE = "/clients";

export type ClientDocumentType = "cpf" | "national_id" | "passport" | "other";

export type ClientGender = "female" | "male" | "non_binary" | "unspecified";

export const CLIENT_DOCUMENT_TYPES: ClientDocumentType[] = [
  "cpf",
  "national_id",
  "passport",
  "other",
];

export const CLIENT_GENDERS: ClientGender[] = [
  "female",
  "male",
  "non_binary",
  "unspecified",
];

export interface ClientAddress {
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  complement?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

/** Client entity returned by GET /api/v1/clients. */
export interface Client extends ClientAddress {
  id: number;
  tenant_id: number;
  name: string;
  country_code: string;
  phone_number: string;
  secondary_country_code?: string | null;
  secondary_phone_number?: string | null;
  email?: string | null;
  email_suppressed_at?: string | null;
  marketing_email_opt_out?: boolean;
  appointment_email_opt_out?: boolean;
  sms_marketing_opt_out?: boolean;
  document_type?: ClientDocumentType | null;
  document_number?: string | null;
  birth_date?: string | null;
  gender?: ClientGender | null;
  instagram_handle?: string | null;
  acquisition_source?: string | null;
  allow_online_booking?: boolean;
  source_registered_on?: string | null;
  notes?: string | null;
  /** Structured service prefs (GAP-F2-065) — distinct from notes / tags / clinical. */
  service_preferences?: ClientServicePreferences | null;
  /** Structured tags (GAP-F2-125) — distinct from CRM stages / notes. */
  tags?: Array<{
    id: number;
    name: string;
    color: string | null;
    is_active: boolean;
  }>;
  /** Count of client credits with sessions remaining (list endpoint). */
  active_credits_count?: number;
  /** Latest paid service name on list (chrome lista). */
  last_service_name?: string | null;
  /** Count of health records (anamnese lite) on list/show. */
  health_records_count?: number;
  /** Active clinical protocols (GAP-F2-114 Fatia A polish). */
  active_protocols_count?: number;
  has_active_protocol?: boolean;
  /** Loyalty cards in progress or with free reward (list/show). */
  loyalty_cards_count?: number;
  has_loyalty_card?: boolean;
  /** Active B2C club membership (list/show — GAP-F2-094 polish). */
  has_active_club?: boolean;
  /** B2C club status on show (GAP-F2-094). */
  club_summary?: {
    has_active: boolean;
    status: string | null;
    plan_name: string | null;
    subscription_id: number | null;
    current_period_end?: string | null;
    visits_used?: number | null;
    visits_limit?: number | null;
    visit_dates?: string[];
    price_locked_cents?: number | null;
    proration_credit_cents?: number;
    included_service_ids?: number[];
  };
  /** GAP-F2-121 — autorização de uso de imagem (marketing before/after). */
  image_marketing_consent_status?: "active" | "expired" | "revoked" | "absent";
  can_use_in_marketing?: boolean;
  created_at: string;
  updated_at: string;
}

/** Payload for POST /api/v1/clients. */
export interface CreateClientPayload {
  name: string;
  country_code?: string;
  phone_number: string;
  secondary_country_code?: string | null;
  secondary_phone_number?: string | null;
  email?: string | null;
  document_type?: ClientDocumentType | null;
  document_number?: string | null;
  birth_date?: string | null;
  gender?: ClientGender | null;
  instagram_handle?: string | null;
  acquisition_source?: string | null;
  allow_online_booking?: boolean;
  source_registered_on?: string | null;
  marketing_email_opt_out?: boolean;
  appointment_email_opt_out?: boolean;
  sms_marketing_opt_out?: boolean;
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  complement?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  notes?: string | null;
  service_preferences?: ClientServicePreferences | null;
}

/** Payload for PATCH /api/v1/clients/{id} — all fields optional. */
export type UpdateClientPayload = Partial<CreateClientPayload>;

export interface ClientListParams extends PaginationParams {
  search?: string;
  has_active_credits?: boolean;
  has_active_club?: boolean;
  has_active_protocol?: boolean;
  has_health_records?: boolean;
  /** Soft-gate régua: só clientes sem data de nascimento. */
  missing_birth_date?: boolean;
  /** GAP-F2-146 polish: sem Instagram. */
  missing_instagram?: boolean;
  /** GAP-F2-146 polish: sem origem / como nos conheceu. */
  missing_acquisition_source?: boolean;
  /**
   * ADJ-F2-147: ficha incompleta (OR) — sem aniversário OU sem Instagram OU sem origem.
   * Quando true, a API ignora os filtros missing_* individuais.
   */
  incomplete_profile?: boolean;
  /** GAP-F2-146 polish: agendamento online desligado. */
  online_booking_blocked?: boolean;
  /** Single tag or AND of multiple (`tag_id` / `tag_id[]`). */
  tag_id?: number | number[];
  credits_expiring_within_days?: number;
}

export interface CheckPhoneParams {
  phone_number: string;
  country_code?: string;
  client_id?: number;
}

export interface CheckPhoneResult {
  exists: boolean;
  client_id: number | null;
}

export const checkPhone = (
  params: CheckPhoneParams
): Promise<ApiResponse<CheckPhoneResult>> =>
  api.get<ApiResponse<CheckPhoneResult>>(`${RESOURCE}/check-phone`, params);

export const getClients = (
  params?: ClientListParams
): Promise<PaginatedApiResponse<Client>> =>
  api.get<PaginatedApiResponse<Client>>(RESOURCE, params);

/** Alias for paginated client search (ClientPicker, header typeahead). */
export const listClients = getClients;

export const getClientById = (id: number): Promise<ApiResponse<Client>> =>
  api.get<ApiResponse<Client>>(`${RESOURCE}/${id}`);

export interface ClientServiceHistoryEntry {
  id: number;
  order_id: number;
  execution_notes: string;
  service?: { id: number; name: string } | null;
  professional?: { id: number; name: string } | null;
  order?: {
    id: number;
    status: string;
    created_at: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface ClientServiceHistoryParams extends PaginationParams {}

export type ClinicalGatePreview = {
  has_clinical_gate_alert: boolean;
  clinical_gate_alerts: Array<{
    field_slug: string;
    label: string;
    answer_label: string;
    matched_service_ids?: number[];
    matched_product_ids?: number[];
  }>;
  clinical_gate_mode: "warn" | "soft_block";
};

export const getClientClinicalGate = (
  clientId: number,
  params?: { service_ids?: number[]; product_ids?: number[] }
): Promise<ApiResponse<ClinicalGatePreview>> =>
  api.get<ApiResponse<ClinicalGatePreview>>(`${RESOURCE}/${clientId}/clinical-gate`, params);

export const getClientServiceHistory = (
  clientId: number,
  params?: ClientServiceHistoryParams
): Promise<PaginatedApiResponse<ClientServiceHistoryEntry>> =>
  api.get<PaginatedApiResponse<ClientServiceHistoryEntry>>(
    `${RESOURCE}/${clientId}/service-history`,
    params
  );

export const createClient = (
  payload: CreateClientPayload
): Promise<ApiResponse<Client>> =>
  api.create<ApiResponse<Client>>(RESOURCE, payload);

export const updateClient = (
  id: number,
  payload: UpdateClientPayload
): Promise<ApiResponse<Client>> =>
  api.update<ApiResponse<Client>>(`${RESOURCE}/${id}`, payload);

export const deleteClient = (
  id: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(`${RESOURCE}/${id}`);

export const importCsv = (
  file: File
): Promise<ApiResponse<Record<string, never>>> => {
  const formData = new FormData();
  formData.append("file", file);

  return axios.post(`${RESOURCE}/import`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }) as Promise<ApiResponse<Record<string, never>>>;
};

export type ClientMergePreview = {
  keep: {
    id: number;
    name: string;
    phone: string;
    email?: string | null;
    notes?: string | null;
    tags: string[];
  };
  absorb: {
    id: number;
    name: string;
    phone: string;
    email?: string | null;
    notes?: string | null;
    tags: string[];
  };
  field_plan: Record<
    string,
    { action: string; keep: unknown; absorb: unknown; result: unknown }
  >;
  relations_to_move: Record<string, number>;
  tags_union: string[];
  blockers: Array<{ code: string; message: string }>;
  can_merge: boolean;
};

export const previewClientMerge = (
  keepClientId: number,
  absorbClientId: number
): Promise<ApiResponse<ClientMergePreview>> =>
  api.create(`${RESOURCE}/merge/preview`, {
    keep_client_id: keepClientId,
    absorb_client_id: absorbClientId,
  }) as Promise<ApiResponse<ClientMergePreview>>;

export const mergeClients = (
  keepClientId: number,
  absorbClientId: number
): Promise<ApiResponse<{ keep: ClientMergePreview["keep"]; absorbed_id: number }>> =>
  api.create(`${RESOURCE}/merge`, {
    keep_client_id: keepClientId,
    absorb_client_id: absorbClientId,
  }) as Promise<ApiResponse<{ keep: ClientMergePreview["keep"]; absorbed_id: number }>>;
